import { urlState } from "app/client/models/gristUrlState";
import { EmailVerifiedPage } from "app/client/ui/EmailVerifiedPage";
import { ForgotPasswordPage } from "app/client/ui/ForgotPasswordPage";
import { LoginPage } from "app/client/ui/LoginPage";
import * as css from "app/client/ui/LoginPagesCss";
import { ConsentForm, SelectAccountForm } from "app/client/ui/OAuthForms";
import { RegisterPage, SelectAccountPage } from "app/client/ui/SignInWithGrist";
import { SignInWithGristAPI } from "app/client/ui/SignInWithGristAPI";
import { SignupPage } from "app/client/ui/SignupPage";
import { getPageTitleSuffix } from "app/common/gristUrls";
import { LoginPageConfig } from "app/common/LoginPageConfig";
import { getGristConfig } from "app/common/urlUtils";

import { dom } from "grainjs";

export function createLoginPage() {
  return dom.domComputed(urlState().state, (state) => {
    const { login } = state;

    if (login) {
      switch (login) {
        case "login": {
          return dom.create(LoginPage);
        }
        case "signup": {
          return dom.create(SignupPage);
        }
        case "verified": {
          return dom.create(EmailVerifiedPage);
        }
        case "forgot-password": {
          return dom.create(ForgotPasswordPage);
        }
        default: {
          // Make sure we don't forget to handle a page. Leaving out this default
          // case is typically enough for TypeScript to catch such errors, but
          // since `undefined` is a valid `DomContents` value and is implicitly
          // returned in the absence of a default case, we need to explicitly
          // check that the type of the page is `never`.
          const exhaustiveCheck: never = login;
          throw new Error(`Unhandled login page: ${exhaustiveCheck}`);
        }
      }
    }

    const { oauth } = getGristConfig() as LoginPageConfig;
    if (oauth?.interaction) {
      const { client, interaction: { cid, prompt } } = oauth;
      const { name } = prompt;

      switch (name) {
        case "select_account":
        case "consent": {
          const suffix = getPageTitleSuffix(getGristConfig());
          document.title = `Sign in${suffix}`;

          const api = new SignInWithGristAPI();
          const params = { api, cid, client };

          return css.pageContainer(
            css.flexJustifyCenter(
              css.formContainer(
                name === "select_account" ?
                  dom.create(SelectAccountForm, { ...params, prompt }) :
                  dom.create(ConsentForm, { ...params, prompt }),
              ),
            ),
          );
        }
        default: {
          const exhaustiveCheck: never = name;
          throw new Error(`Unhandled OAuth interaction prompt: ${exhaustiveCheck}`);
        }
      }
    }

    // Our current routing system lives in core code, and we can't easily extend it with
    // routes outside of core code. Until we have a better solution, we resort to inspecting
    // the URL directly.
    const url = new URL(location.href);
    const pathnames = url.pathname.slice(1).split("/");
    const startIndex = pathnames[0] === "o" ? 2 : 0;
    if (pathnames[startIndex] === "oauth") {
      const page = pathnames[startIndex + 1];

      switch (page) {
        case "register":
        case "select-account": {
          const suffix = getPageTitleSuffix(getGristConfig());
          document.title = `Sign in with getgrist.com${suffix}`;

          return page === "register" ?
            dom.create(RegisterPage) :
            dom.create(SelectAccountPage);
        }
      }
    }

    throw new Error("Page not found");
  });
}
